/*!
 * Copyright © 2018-2022. Verizon Connect Ireland Limited. All rights reserved.
 */

import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { isElementNull } from '../../../../utils';

import {
  DeviceHealthIssueCategories,
  EPermissionStatus,
  EPlotType,
  ESpeedLimitTypes,
  EVehicleFuelType,
  EVehicleStatusType,
  IBalloon,
  IScheduledJob,
  IUserPermissions,
  IUserSettings,
  IVehicleWorkOrderBalloon
} from '../../../core/models';
import { VehiclePlotTimerFormatService } from '../../../core/services/vehicle-plot-timer-format';
import { IDeviceHealth } from '../../../core/store/state';

@Component({
  selector: 'fm-vehicle-work-order-balloon',
  templateUrl: './vehicle-work-order-balloon.component.html',
  styleUrls: ['./vehicle-work-order-balloon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VehicleWorkOrderBalloonComponent implements OnChanges {
  @Input()
  public balloon: IVehicleWorkOrderBalloon;
  @Input()
  public isStreetViewAvailable: boolean;
  @Input()
  public permissions: IUserPermissions;
  @Input()
  public userCountry: string;
  @Input()
  public vehiclePlotsTimer: number;
  @Input()
  public speedLimitOption: ESpeedLimitTypes;
  @Input()
  public showTachoInformation: boolean;
  @Input()
  public showVehicleBalloonLinks: boolean;
  @Input()
  public showVehicleBalloonEcm: boolean;
  @Input()
  public customBalloonFeatureEnabled: boolean;
  @Input()
  public vehicleFuelType: EVehicleFuelType;
  @Input()
  public disableZoomTo: boolean;
  @Input()
  userSettings: IUserSettings;
  @Input()
  vehicleLastStateChangeDuration: number;
  @Input()
  vehicleHealthStatus: IDeviceHealth;

  @Output()
  public replay = new EventEmitter<IVehicleWorkOrderBalloon>();
  @Output()
  public streetViewClicked = new EventEmitter<IBalloon>();
  @Output()
  public showMessage = new EventEmitter<IVehicleWorkOrderBalloon>();
  @Output()
  public zoomTo = new EventEmitter<IVehicleWorkOrderBalloon>();
  @Output()
  public scheduledJobClicked = new EventEmitter<IScheduledJob>();
  @Output()
  public resized = new EventEmitter<IVehicleWorkOrderBalloon>();

  @HostBinding('class.balloon-address-panel-ready')
  balloonAddressReady = false;

  public EVehicleStatusType = EVehicleStatusType;
  public EPermissionStatus = EPermissionStatus;
  public EPlotType = EPlotType;
  public readonly defaultAvatar = 'assets/images/balloons/avatar-placeholder.svg';
  public showLinks: boolean;
  public chargeDurationSeconds: number;
  public formattedDuration: string;
  public isChargingOrStoppedOrIdle: boolean;
  public hasHealthStatusViewPermission: boolean;
  public hasCameraIssuesViewPermission: boolean;
  public showDeviceHealthDetails: boolean;

  constructor(private readonly _timerFormatService: VehiclePlotTimerFormatService) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.showVehicleBalloonLinks) {
      this.showLinks = this.showVehicleBalloonLinks;
      if (!changes.showVehicleBalloonLinks.isFirstChange() && !isElementNull(this.balloon)) {
        this._triggerResize();
      }
    }

    if (changes.balloon) {
      this._isballoonAddressReady();
      this.isChargingOrStoppedOrIdle =
        this.balloon?.status.vehicleStatus === EVehicleStatusType.Charging ||
        this.balloon?.status.vehicleStatus === EVehicleStatusType.Stopped ||
        this.balloon?.status.vehicleStatus === EVehicleStatusType.Idle;
      this.hasHealthStatusViewPermission = this._getPermissionForHealthStatus();
      this.hasCameraIssuesViewPermission = this._getPermissionForVideoExperience();
      this.showDeviceHealthDetails = this._doesDevcieHasIssue();
    }
    this._formatPlotTimer();
  }

  public onMessage(): void {
    this.showMessage.emit(this.balloon);
  }

  public onReplay(): void {
    this.replay.emit(this.balloon);
  }

  public onStreetView(): void {
    this.streetViewClicked.emit(this.balloon);
  }

  public onZoomTo(): void {
    this.zoomTo.emit(this.balloon);
  }

  public onScheduledJobClicked(scheduledJob: IScheduledJob) {
    this.scheduledJobClicked.emit(scheduledJob);
  }

  private _doesDevcieHasIssue() {
    if (this.vehicleHealthStatus) {
      if (this.vehicleHealthStatus.vehicleId == this.balloon?.id?.toString()) {
        return DeviceHealthIssueCategories.findIndex(x => x === this.vehicleHealthStatus.healthStatus.toUpperCase()) > -1;
      }
    }
    return false;
  }

  private _getPermissionForHealthStatus(): boolean {
    return this.permissions?.manageVehicles === EPermissionStatus.Full;
  }

  private _getPermissionForVideoExperience(): boolean {
    return this.permissions?.videoExperience === EPermissionStatus.Full;
  }

  private _triggerResize(): void {
    this.resized.emit(this.balloon);
  }

  private _isballoonAddressReady() {
    this.balloonAddressReady = this.balloon?.status.formattedAddress && this.balloon?.status.formattedAddress.length > 0;
  }

  private _formatPlotTimer(): void {
    this.formattedDuration = this._timerFormatService.format(this.vehicleLastStateChangeDuration, this.vehiclePlotsTimer);
  }
}
